import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLocalization } from '../context/LocalizationContext';
import { activityLogsAPI, availabilityAPI } from '../services/api';
import ConfirmDialog from './ConfirmDialog';
import { formatDateTime } from '../utils/dateFormat';
import { formatCoordinates, getAppDeviceId, getCurrentCoordinates } from '../utils/geolocation';
import styles from '../styles/Navbar.module.css';

const Navbar = () => {
  const { user, logout, can } = useAuth();
  const { t, currentLanguage, setCurrentLanguage, languages } = useLocalization();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [forceCollapse, setForceCollapse] = useState(false);
  const [logsOpen, setLogsOpen] = useState(false);
  const [reportMenuOpen, setReportMenuOpen] = useState(false);
  const [setupMenuOpen, setSetupMenuOpen] = useState(false);
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const [clockSubmitting, setClockSubmitting] = useState(false);
  const [isClockedIn, setIsClockedIn] = useState(false);
  const [todayAvailability, setTodayAvailability] = useState(null);
  const [confirmState, setConfirmState] = useState({
    open: false,
    action: null,
    title: '',
    message: '',
    confirmText: 'Confirm',
    tone: 'default',
  });
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [attendanceMenuOpen, setAttendanceMenuOpen] = useState(false);

  const containerRef = useRef(null);
  const rightAreaRef = useRef(null);
  const menuRef = useRef(null);
  const reportMenuContainerRef = useRef(null);
  const setupMenuContainerRef = useRef(null);
  const logsWrapRef = useRef(null);
  const languageMenuRef = useRef(null);
  const planningMenuContainerRef = useRef(null);
  const forceCollapseRef = useRef(false);
  const collapseThresholdRef = useRef(null);
  const canViewAvailability = can('availability', 'view');
  const canViewLogs = can('logs', 'view');

  const setCollapseState = (val) => {
    forceCollapseRef.current = val;
    setForceCollapse(val);
  };

  const handleLogout = () => {
    setMobileMenuOpen(false);
    logout();
    navigate('/login');
  };

  const closeMenu = () => {
    setMobileMenuOpen(false);
    setReportMenuOpen(false);
    setSetupMenuOpen(false);
    setLogsOpen(false);
    setAttendanceMenuOpen(false);
    setLanguageMenuOpen(false);
  };

  const toggleReportMenu = () => {
    setReportMenuOpen((open) => {
      const next = !open;
      if (next) {
        setSetupMenuOpen(false);
        setLogsOpen(false);
        setAttendanceMenuOpen(false);
      }
      return next;
    });
  };

  const toggleSetupMenu = () => {
    setSetupMenuOpen((open) => {
      const next = !open;
      if (next) {
        setReportMenuOpen(false);
        setLogsOpen(false);
        setAttendanceMenuOpen(false);
      }
      return next;
    });
  };

  const toggleLogsMenu = () => {
    setLogsOpen((open) => {
      const next = !open;
      if (next) {
        setReportMenuOpen(false);
        setSetupMenuOpen(false);
        setAttendanceMenuOpen(false);
      }
      return next;
    });
  };

  const toggleAttendanceMenu = () => {
    setAttendanceMenuOpen((open) => {
      const next = !open;
      if (next) {
        setReportMenuOpen(false);
        setSetupMenuOpen(false);
        setLogsOpen(false);
      }
      return next;
    });
  };

  // Auto-collapse nav links into hamburger when they would overlap the right area
  useEffect(() => {
    forceCollapseRef.current = false;
    setForceCollapse(false);
    collapseThresholdRef.current = null;

    const check = () => {
      const container = containerRef.current;
      const menu = menuRef.current;
      const right = rightAreaRef.current;
      if (!container || !right || !menu) return;

      if (!forceCollapseRef.current) {
        const menuRect = menu.getBoundingClientRect();
        const rightRect = right.getBoundingClientRect();
        if (menuRect.right + 16 >= rightRect.left) {
          collapseThresholdRef.current = container.offsetWidth + 40;
          setCollapseState(true);
        }
      } else {
        if (collapseThresholdRef.current !== null && container.offsetWidth >= collapseThresholdRef.current) {
          setCollapseState(false);
          setMobileMenuOpen(false);
          collapseThresholdRef.current = null;
        }
      }
    };

    const timeout = setTimeout(check, 0);
    const ro = new ResizeObserver(check);
    const el = containerRef.current;
    if (el) ro.observe(el);
    return () => {
      clearTimeout(timeout);
      ro.disconnect();
    };
  }, [user]);

  useEffect(() => {
    if (!logsOpen || !user) return;

    const fetchLogs = async () => {
      try {
        setLogsLoading(true);
        const response = await activityLogsAPI.getAll(50, 3);
        setLogs(response.data || []);
      } catch (error) {
        setLogs([]);
      } finally {
        setLogsLoading(false);
      }
    };

    fetchLogs();
  }, [logsOpen, user, canViewLogs]);

  const fetchClockStatus = useCallback(async () => {
    if (!user || !canViewAvailability) return;
    try {
      const response = await availabilityAPI.getToday();
      const today = response?.data;
      const active = !!today && Number(today.available) === 1 && !today.logout_time;
      setIsClockedIn(active);
      setTodayAvailability(today || null);
    } catch (error) {
      setIsClockedIn(false);
      setTodayAvailability(null);
    }
  }, [user, canViewAvailability]);

  const getClockPayload = async () => {
    const deviceId = await getAppDeviceId();
    try {
      const coords = await Promise.race([
        getCurrentCoordinates({ timeoutMs: 6000, maximumAge: 30000, enableHighAccuracy: false }),
        new Promise((resolve) => setTimeout(() => resolve(null), 2000)),
      ]);
      if (!coords) return deviceId ? { deviceId } : {};
      return {
        latitude: coords.latitude,
        longitude: coords.longitude,
        ...(deviceId ? { deviceId } : {}),
      };
    } catch (error) {
      return deviceId ? { deviceId } : {};
    }
  };

  useEffect(() => {
    fetchClockStatus();
  }, [fetchClockStatus]);

  useEffect(() => {
    setMobileMenuOpen(false);
    setReportMenuOpen(false);
    setSetupMenuOpen(false);
    setLogsOpen(false);
    setAttendanceMenuOpen(false);
    setLanguageMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const target = event.target;

      if (logsOpen && logsWrapRef.current && !logsWrapRef.current.contains(target)) {
        setLogsOpen(false);
      }

      if (reportMenuOpen && reportMenuContainerRef.current && !reportMenuContainerRef.current.contains(target)) {
        setReportMenuOpen(false);
      }

      if (setupMenuOpen && setupMenuContainerRef.current && !setupMenuContainerRef.current.contains(target)) {
        setSetupMenuOpen(false);
      }

      if (attendanceMenuOpen && planningMenuContainerRef.current && !planningMenuContainerRef.current.contains(target)) {
        setAttendanceMenuOpen(false);
      }

      if (languageMenuOpen && languageMenuRef.current && !languageMenuRef.current.contains(target)) {
        setLanguageMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [logsOpen, reportMenuOpen, setupMenuOpen, attendanceMenuOpen, planningMenuContainerRef, languageMenuOpen]);

  const handleQuickClock = async () => {
    if (clockSubmitting) return;
    try {
      setClockSubmitting(true);
      const payload = await getClockPayload();
      if (isClockedIn) {
        await availabilityAPI.clockOut(payload);
      } else {
        await availabilityAPI.clockIn(payload);
      }
      await fetchClockStatus();
    } catch (error) {
      alert(error.response?.data?.message || t('nav_clock_failed', 'Failed to update clock status'));
    } finally {
      setClockSubmitting(false);
    }
  };

  const closeConfirmDialog = () => {
    if (clockSubmitting) return;
    setConfirmState((prev) => ({ ...prev, open: false }));
  };

  const requestLogout = () => {
    setConfirmState({
      open: true,
      action: 'logout',
      title: t('nav_exit_session_title', 'Exit session?'),
      message: t('nav_exit_session_message', 'You will be logged out from Daily Tracker.'),
      confirmText: t('nav_exit_confirm', 'Exit'),
      tone: 'default',
    });
  };

  const requestQuickClock = () => {
    setConfirmState({
      open: true,
      action: isClockedIn ? 'clock-out' : 'clock-in',
      title: isClockedIn
        ? t('nav_confirm_clock_out', 'Confirm Clock Out') : t('nav_confirm_clock_in', 'Confirm Clock In'),
      message: isClockedIn
        ? t('nav_confirm_clock_out_message', 'Do you want to clock out now?') : t('nav_confirm_clock_in_message', 'Do you want to clock in now?'),
      confirmText: isClockedIn ? t('nav_clock_out', 'Clock Out') : t('nav_clock_in', 'Clock In'),
      tone: 'default',
    });
  };

  const handleConfirmDialog = async () => {
    if (!confirmState.action) return;

    if (confirmState.action === 'logout') {
      closeConfirmDialog();
      handleLogout();
      return;
    }

    if (confirmState.action === 'clock-in' || confirmState.action === 'clock-out') {
      await handleQuickClock();
      setConfirmState((prev) => ({ ...prev, open: false }));
    }
  };

  const reportLinks = [
    can('reports', 'view') && { to: '/reports/attendance', label: t('report_attendance', 'Attendance Report') },
    can('reports', 'view') && { to: '/reports/tasks', label: t('report_tasks', 'Task Report') },
    can('reports', 'view') && { to: '/reports/users', label: t('report_users', 'User Report') },
    can('reports', 'view') && { to: '/reports/resources', label: t('report_resources', 'Resource Report') },
    can('reports', 'view') && { to: '/reports/costs', label: t('report_costs', 'Cost Report') },
  ].filter(Boolean);

  const setupLinks = [
    can('logs', 'view') && { to: '/activities', label: t('nav_activities', 'Activities') },
    can('permissions', 'view') && { to: '/roles', label: t('nav_roles', 'Roles') },
    can('users', 'view') && { to: '/users', label: t('nav_users', 'Users') },
    can('resources', 'view') && { to: '/resources', label: t('nav_resources', 'Resources') },
    can('resource_types', 'view') && { to: '/resource-types', label: t('nav_resource_type', 'Resource Type') },
    can('job_types', 'view') && { to: '/job-types', label: t('nav_job_type', 'Job Type') },
    can('permissions', 'edit') && { to: '/settings/localization', label: t('nav_language', 'Language') },
  ].filter(Boolean);
  const isVerticalMenuMode = forceCollapse || mobileMenuOpen;

  const formatLogTime = (value) => formatDateTime(value);
  const hoverLocationName = isClockedIn
    ? (todayAvailability?.login_location_name || '')
    : (todayAvailability?.logout_location_name || todayAvailability?.login_location_name || '');
  const hoverCoordinates = isClockedIn
    ? formatCoordinates(todayAvailability?.login_latitude, todayAvailability?.login_longitude)
    : (formatCoordinates(todayAvailability?.logout_latitude, todayAvailability?.logout_longitude)
      || formatCoordinates(todayAvailability?.login_latitude, todayAvailability?.login_longitude));
  const quickClockTitle = [
    isClockedIn ? t('nav_clock_out', 'Clock Out') : t('nav_clock_in', 'Clock In'),
    hoverLocationName ? `Location: ${hoverLocationName}` : '',
    hoverCoordinates ? `GPS: ${hoverCoordinates}` : '',
  ].filter(Boolean).join('\n');

  return (
    <nav className={styles.navbar}>
      <div className={styles.container} ref={containerRef}>
        <div className={styles.leftArea}>
          {user && (
            <button
              className={`${styles.mobileMenuBtn} ${forceCollapse ? styles.mobileMenuBtnForce : ''}`}
              onClick={() => setMobileMenuOpen((open) => !open)}
              aria-label={t('nav_toggle_menu', 'Toggle menu')}
              aria-expanded={mobileMenuOpen}
            >
              <i className={`bi ${mobileMenuOpen ? 'bi-x-lg' : 'bi-list'}`} aria-hidden="true"></i>
            </button>
          )}

          {user && (
            <Link to="/" className={styles.logo} onClick={closeMenu}>
              <i className="bi bi-grid-1x2-fill" aria-hidden="true"></i> {t('app_name', 'Daily Tracker')}
            </Link>
          )}

          <div
            ref={menuRef}
            className={[
              styles.menu,
              forceCollapse
                ? (mobileMenuOpen ? styles.menuForcedOpen : styles.menuForced)
                : (mobileMenuOpen ? styles.menuOpen : '')
            ].filter(Boolean).join(' ')}
          >
            {user && (
              <>
                <Link to="/dashboard" className={styles.navLink} onClick={closeMenu}>
                  {user.role === 'user'
                    ? t('nav_dashboard', 'Dashboard')
                    : user.role === 'supervisor'
                      ? t('nav_supervisor_dashboard', 'Supervisor Dashboard')
                      : t('nav_admin_dashboard', 'Admin Dashboard')}
                </Link>
                {can('tasks', 'view') && (
                    <Link to="/tasks" className={styles.navLink} onClick={closeMenu}>
                      {t('nav_tasks', 'Tasks')}
                    </Link>
                )}
                {can('teams', 'view') && (
                    <Link to="/teams" className={styles.navLink} onClick={closeMenu}>
                      {t('nav_teams', 'Teams')}
                    </Link>
                )}
                {/* Only Attendance submenu remains */}

                {/* Planning Dropdown - only submenu, not main menu */}
                {can('availability', 'view') && (
                  <div className={styles.submenuContainer} ref={planningMenuContainerRef}>
                    <button
                      className={`${styles.navLink} ${styles.submenuToggle}`}
                      type="button"
                      onClick={toggleAttendanceMenu}
                      aria-expanded={attendanceMenuOpen}
                      aria-label={t('nav_toggle_planning_menu', 'Toggle planning menu')}
                    >
                      {t('nav_planning', 'Planning')}
                      <i className={`bi bi-chevron-down ${attendanceMenuOpen ? styles.open : ''}`} aria-hidden="true"></i>
                    </button>
                    <div className={`${styles.submenu} ${attendanceMenuOpen ? styles.submenuOpen : ''}`}> 
                      <Link to="/clock" className={styles.submenuLink} onClick={() => { closeMenu(); setAttendanceMenuOpen(false); setReportMenuOpen(false); setSetupMenuOpen(false); setLogsOpen(false); }}>
                        {t('nav_daily_attendance', 'Daily Attendance')}
                      </Link>
                      {can('users', 'view') && (
                        <Link to="/admin/availability" className={styles.submenuLink} onClick={() => { closeMenu(); setAttendanceMenuOpen(false); setReportMenuOpen(false); setSetupMenuOpen(false); setLogsOpen(false); }}>
                          {t('nav_all_users_availability', "All Users' Availability")}
                        </Link>
                      )}
                    </div>
                  </div>
                )}

                {reportLinks.length > 0 && (
                  <div className={styles.submenuContainer} ref={reportMenuContainerRef}>
                    <button
                      className={`${styles.navLink} ${styles.submenuToggle}`}
                      type="button"
                      onClick={toggleReportMenu}
                      aria-expanded={reportMenuOpen}
                      aria-label={t('nav_toggle_report_menu', 'Toggle report menu')}
                    >
                      {isVerticalMenuMode ? (
                        <span className={styles.configLabel}>{t('nav_report', 'Report')}</span>
                      ) : (
                        <span>{t('nav_report', 'Report')}</span>
                      )}
                      <i className={`bi bi-chevron-down ${reportMenuOpen ? styles.open : ''}`} aria-hidden="true"></i>
                    </button>
                    <div className={`${styles.submenu} ${reportMenuOpen ? styles.submenuOpen : ''}`}>
                      {reportLinks.map((item) => (
                        <Link key={item.to} to={item.to} className={styles.submenuLink} onClick={closeMenu}>
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* Setup Submenu */}
                {setupLinks.length > 0 && (
                  <div className={styles.submenuContainer} ref={setupMenuContainerRef}>
                    <button
                      className={`${styles.navLink} ${styles.submenuToggle}`}
                      type="button"
                      onClick={toggleSetupMenu}
                      aria-expanded={setupMenuOpen}
                      aria-label={t('nav_toggle_setup_menu', 'Toggle setup menu')}
                    >
                      {isVerticalMenuMode ? (
                        <span className={styles.configLabel}>{t('nav_config', 'Config')}</span>
                      ) : (
                        <i className="bi bi-grid-3x3-gap-fill" aria-hidden="true"></i>
                      )}
                      <i className={`bi bi-chevron-down ${setupMenuOpen ? styles.open : ''}`} aria-hidden="true"></i>
                    </button>
                    <div className={`${styles.submenu} ${setupMenuOpen ? styles.submenuOpen : ''}`}>
                      {setupLinks.map((item) => (
                        <Link key={item.to} to={item.to} className={styles.submenuLink} onClick={closeMenu}>
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {user && (
          <div className={styles.rightArea} ref={rightAreaRef}>
            {can('availability', 'view') && (
              <button
                type="button"
                className={`${styles.quickClockBtn} ${isClockedIn ? styles.quickClockBtnOut : styles.quickClockBtnIn}`}
                onClick={requestQuickClock}
                disabled={clockSubmitting}
                title={quickClockTitle}
                aria-label={isClockedIn ? t('nav_quick_clock_out', 'Quick clock out') : t('nav_quick_clock_in', 'Quick clock in')}
              >
                <i className="bi bi-alarm" aria-hidden="true"></i>
                <span>{isClockedIn ? t('nav_clock_out', 'Clock Out') : t('nav_clock_in', 'Clock In')}</span>
              </button>
            )}

            <div className={styles.logsWrap} ref={logsWrapRef}>
              <button
                className={styles.notifyBtn}
                aria-label={t('nav_logs', 'Logs')}
                title={t('nav_logs', 'Logs')}
                onClick={toggleLogsMenu}
              >
                <i className="bi bi-journal-text"></i>
                <span className={styles.notifyDot} aria-hidden="true"></span>
              </button>
              {logsOpen && (
                <div className={styles.logsDropdown}>
                  <div className={styles.logsHeader}>{t('nav_recent_logs', 'Recent Logs (last 3 days)')}</div>
                  {logsLoading ? (
                    <div className={styles.logsEmpty}>{t('nav_loading_logs', 'Loading logs...')}</div>
                  ) : logs.length === 0 ? (
                    <div className={styles.logsEmpty}>{t('nav_no_logs', 'No logs available')}</div>
                  ) : (
                    <ul className={styles.logsList}>
                      {logs.map((log) => (
                        <li key={log.id} className={styles.logItem}>
                          <div className={styles.logTitle}>
                              {log.description || `${log.actor_username} ${log.action_name} ${log.module_name}`}
                          </div>
                          <div className={styles.logMeta}>
                            <span className={styles[`logUser_${log.actor_role}`]}>{log.actor_username}</span>
                            <span>{formatLogTime(log.created_at)}</span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className={styles.logsFooter}>
                    <Link to="/activities" className={styles.logsViewAll} onClick={() => setLogsOpen(false)}>
                      {t('nav_view_all_activities', 'View all activities')}
                    </Link>
                  </div>
                </div>
              )}
            </div>

            <select
              className={`${styles.languagePicker} ${styles.languagePickerDesktop}`}
              value={currentLanguage}
              onChange={(e) => setCurrentLanguage(e.target.value)}
              aria-label={t('common_language', 'Language')}
            >
              {languages.filter((language) => language.isActive).map((language) => (
                <option key={language.code} value={language.code}>{language.code.toUpperCase()}</option>
              ))}
            </select>

            <div className={styles.languageMenuWrap} ref={languageMenuRef}>
              <button
                type="button"
                className={styles.languageMenuBtn}
                onClick={() => setLanguageMenuOpen((open) => !open)}
                aria-label={t('common_language', 'Language')}
                aria-expanded={languageMenuOpen}
              >
                <span>{currentLanguage.toUpperCase()}</span>
                <i className={`bi bi-chevron-down ${languageMenuOpen ? styles.open : ''}`} aria-hidden="true"></i>
              </button>
              {languageMenuOpen && (
                <div className={styles.languageMenuDropdown}>
                  {languages.filter((language) => language.isActive).map((language) => (
                    <button
                      key={language.code}
                      type="button"
                      className={`${styles.languageMenuItem} ${currentLanguage === language.code ? styles.languageMenuItemActive : ''}`}
                      onClick={() => {
                        setCurrentLanguage(language.code);
                        setLanguageMenuOpen(false);
                      }}
                    >
                      {language.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className={styles.userMenu}>
              <span className={styles.userName}>{user.username}</span>
              <span className={styles.role}>{user.role}</span>
              <button onClick={requestLogout} className={styles.logoutBtn} title={t('nav_exit', 'Exit')} aria-label={t('nav_exit', 'Exit')}>
                <i className="bi bi-box-arrow-right" aria-hidden="true"></i>
              </button>
            </div>
          </div>
        )}

        {!user && (
          <Link to="/" className={styles.logo}>
            <i className="bi bi-grid-1x2-fill" aria-hidden="true"></i> {t('app_name', 'Daily Tracker')}
          </Link>
        )}

        <ConfirmDialog
          isOpen={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          confirmText={confirmState.confirmText}
          onConfirm={handleConfirmDialog}
          onCancel={closeConfirmDialog}
          loading={clockSubmitting}
          tone={confirmState.tone}
        />
      </div>
    </nav>
  );
};

export default Navbar;
